<template>
	<div class="space-y-2xl">
		<div v-if="loading" class="flex justify-center">
			<v-spinner />
		</div>

		<template v-if="articles.length > 0">
			<v-container>
				<div
					class="grid grid-cols-2 gap-x-2 gap-y-6 md:grid-cols-3 md:gap-x-6 md:gap-y-9 lg:grid-cols-4"
				>
					<v-storyblok-article-card-big
						v-for="(article, index) in articles"
						:key="index"
						class="md:pl-0"
						:article="article"
					/>
				</div>
			</v-container>

			<div class="flex w-full flex-col items-center justify-center">
				<v-button
					theme="black"
					:disabled="!hasMore"
					size="wide"
					type="submit"
					:aria-label="hasMore ? t('load-more') : t('end-of-results')"
					@click="maxPage += 1"
				>
					<template v-if="!hasMore">
						{{ t("end-of-results") }}
					</template>

					<template v-else>
						{{ t("load-more") }}
					</template>
				</v-button>

				<div class="w-[250px] space-y-xs pt-lg">
					<div class="h-[2px] rounded-full bg-gray-300">
						<div
							class="h-[2px] rounded-full bg-black"
							:style="{
								width: `${(articles.length / totalCount) * 100}%`,
							}"
						></div>
					</div>

					<p class="text-center">
						{{ $t("loader.show-more-prefix") }} {{ articles.length }} /
						{{ totalCount }} {{ $t("loader.show-more-suffix") }}
					</p>
				</div>
			</div>
		</template>

		<v-container v-else-if="!loading">
			<div class="flex-y items-center">{{ t("no-articles") }}</div>
		</v-container>
	</div>
</template>

<script setup lang="ts">
import {whenever} from "@vueuse/core";
import type {ISbStoryData} from "storyblok-js-client";

import {useStoryblokArticles} from "@pkg/shared/composables/use-storyblok-articles";

const properties = defineProps<{
	categories: string[];
	searchTerm?: string;
}>();

const loading = ref(true);
const maxPage = ref(1);
const hasMore = ref(false);
const totalCount = ref(0);
const articles = ref<ISbStoryData[]>([]);

// Reset when article fetch conditions change
watch([() => properties.searchTerm, () => properties.categories], () => {
	loading.value = true;
	articles.value = [];
	maxPage.value = 1;
});

const {data: articlesResponse} = useStoryblokArticles({
	featured: false,
	categories: computed(() => properties.categories),
	searchTerm: computed(() => properties.searchTerm),
	perPage: 15,
	page: maxPage,
});

whenever(articlesResponse, () => {
	if (!articlesResponse.value) {
		return;
	}

	const {total, stories} = articlesResponse.value;
	totalCount.value = total;
	articles.value.push(...stories);
	hasMore.value = articles.value.length < totalCount.value;
	loading.value = false;
});

const {t} = useI18n();
</script>

<i18n lang="json">
{
	"et": {
		"load-more": "Lae veel",
		"no-articles": "Artikleid pole",
		"end-of-results": "Tulemuste lõpp!"
	},
	"fi": {
		"load-more": "Lataa lisää",
		"no-articles": "Ei artikkeleita",
		"end-of-results": "Olet päässyt loppuun!"
	}
}
</i18n>
